/**
 * @fileoverview Plan confirmation step for the onboarding flow.
 * Displays selected plan details with Hormozi-style value stacking.
 * @module components/onboarding/steps/PlanConfirmation
 */

"use client";

import { Check, Shield, Sparkles, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { PlanTier } from "@/hooks/useOnboardingState";
import { useOnboardingState } from "@/hooks/useOnboardingState";
import { featureFlags } from "@/config/features";
import { trackOnboarding } from "@/lib/telemetry";

/**
 * Configuration type for plan details display with Hormozi value stacking.
 * @property {string} name - Display name of the plan
 * @property {number} price - Monthly price in USD
 * @property {number} credits - Monthly credit allocation
 * @property {Array} valueStack - Individual value items with their "worth"
 * @property {number} totalValue - Combined value of all items
 */
type ValueItem = {
  feature: string;
  value: number;
};

type PlanDetails = {
  name: string;
  price: number;
  credits: number;
  valueStack: ValueItem[];
  totalValue: number;
  dreamOutcome: string;
};

const PLAN_DATA: Record<PlanTier, PlanDetails> = {
  free: {
    // Paid gate ON: 3-day Stripe trial framing. OFF (default): free-to-start.
    name: featureFlags.enablePaidGate ? "Free Trial" : "Free",
    price: 0,
    credits: 100,
    dreamOutcome: "Try before you commit",
    valueStack: featureFlags.enablePaidGate
      ? [
          { feature: "3-day free trial", value: 50 },
          { feature: "Full feature access", value: 0 },
          { feature: "Cancel anytime before it ends", value: 0 },
        ]
      : [
          { feature: "30 free renders", value: 50 },
          { feature: "Full feature access", value: 0 },
          { feature: "No credit card required", value: 0 },
        ],
    totalValue: 50,
  },
  starter: {
    name: "Designer",
    price: 29,
    credits: 400,
    dreamOutcome: "Win more clients with pro-quality renders",
    valueStack: [
      { feature: "400 renders/month", value: 200 },
      { feature: "4K resolution", value: 49 },
      { feature: "Email support", value: 29 },
    ],
    totalValue: 278,
  },
  pro: {
    name: "Professional",
    price: 79,
    credits: 1500,
    dreamOutcome: "Present like a top firm. Close clients faster.",
    valueStack: [
      { feature: "1,500 renders/month", value: 450 },
      { feature: "Priority processing", value: 79 },
      { feature: "Priority support", value: 49 },
    ],
    totalValue: 578,
  },
  studio: {
    // Internal planId key "studio" maps to the user-facing display name "Enterprise".
    name: "Enterprise",
    price: 299,
    credits: 6000,
    dreamOutcome: "Scale your firm without hiring renderers",
    valueStack: [
      { feature: "6,000 renders/month", value: 1200 },
      { feature: "Team seats (5 users)", value: 500 },
      { feature: "Dedicated support", value: 149 },
    ],
    totalValue: 1849,
  },
};

/**
 * Plan confirmation component showing selected subscription details.
 *
 * @description This is the first step in the signup flow. After a user selects
 * a plan from the pricing page, this component displays a summary of their
 * selection before proceeding to account creation.
 *
 * Available plans:
 * - Free Trial: 3-day Stripe-managed free trial, card required, cancel anytime
 * - Designer ($29): 400 renders/mo, 4K resolution, email support
 * - Professional ($79): 1,500 renders/mo, priority processing & support
 * - Enterprise ($299, internal planId "studio"): 6,000 renders/mo, team seats, dedicated support
 *
 * Features:
 * - Displays plan name, price, and credit allocation
 * - Lists all included features with checkmarks
 * - Gradient card design with brand styling
 * - "Continue to Sign Up" button advances to account creation
 * - "Change Plan" button closes modal (returns to pricing page)
 *
 * @returns Plan confirmation card React element
 *
 * @example
 * // Used as a step within OnboardingModal
 * {state.modalStep === "plan" && <PlanConfirmation />}
 *
 * @example
 * // The component reads selected plan from onboarding state
 * // Plan is typically set when user clicks a pricing tier
 * function PricingCard({ tier }) {
 *   const { actions } = useOnboardingState();
 *   const handleSelect = () => {
 *     actions.setSelectedPlan(tier);
 *     actions.openModal();
 *   };
 *   return <button onClick={handleSelect}>Select {tier}</button>;
 * }
 */
export function PlanConfirmation() {
  const { state, actions } = useOnboardingState();
  const selectedPlan = state.selectedPlan || (featureFlags.enablePaidGate ? "starter" : "free");
  const plan = PLAN_DATA[selectedPlan];
  const isFree = selectedPlan === "free";

  const handleContinue = () => {
    trackOnboarding.planSelected(selectedPlan, state.billingCycle);
    actions.setModalStep("signup");
  };

  const handleChangePlan = () => {
    actions.closeModal();
  };

  // Simplified confirmation for the free / trial entry tier - brief and action-oriented
  if (isFree) {
    return (
      <div className="mx-auto flex max-w-sm flex-col items-center">
        {/* Decorative icon */}
        <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-gold-accent/20 to-gold-accent/5">
          <Sparkles className="h-7 w-7 text-gold-accent" strokeWidth={1.5} />
        </div>

        <h2 className="mb-2 text-center font-semibold text-2xl text-gray-900 tracking-tighter">
          {featureFlags.enablePaidGate ? "Start Your 3-Day Free Trial" : "Start Creating for Free"}
        </h2>

        <p className="mb-6 text-center text-base text-gray-400">
          {featureFlags.enablePaidGate
            ? "Full access for 3 days — card required, cancel anytime"
            : "5 AI renders per day — no credit card required"}
        </p>

        <Button
          className="w-full max-w-[200px] bg-gray-900 font-medium text-white transition-all hover:bg-gray-800 focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 focus:ring-offset-white"
          onClick={handleContinue}
          size="default"
        >
          Get Started
        </Button>
      </div>
    );
  }

  // Full confirmation for paid tiers with Hormozi-style value stacking
  const savings = plan.totalValue - plan.price;
  const savingsPercent = Math.round((savings / plan.totalValue) * 100);

  return (
    <div className="flex flex-col items-center">
      {/* Dream Outcome Headline */}
      <h2 className="mb-2 text-center font-semibold text-2xl text-gray-900 tracking-tight sm:text-3xl">
        {plan.dreamOutcome}
      </h2>
      <p className="mb-8 text-center text-base text-gray-500">with the {plan.name} plan</p>

      <div className="mb-6 w-full max-w-md rounded-xl border-2 border-gold-accent bg-gradient-to-br from-[#faf8f5] to-white p-6 shadow-xl">
        {/* Plan header */}
        <div className="mb-4 flex items-baseline justify-between border-gray-200 border-b pb-4">
          <div>
            <div className="mb-1 flex items-center gap-2">
              <h3 className="font-bold text-gray-900 text-xl">{plan.name}</h3>
              {selectedPlan === "pro" && (
                <span className="rounded bg-gold-accent/20 px-2 py-0.5 font-semibold text-gold-accent text-xs">
                  MOST POPULAR
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-baseline">
              <span className="font-bold text-3xl text-gold-accent">${plan.price}</span>
              <span className="ml-1 text-gray-500 text-sm">/mo</span>
            </div>
          </div>
        </div>

        {/* Value Stack - Hormozi style */}
        <div className="space-y-2">
          <p className="mb-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">
            What you get:
          </p>
          {plan.valueStack.map((item) => (
            <div className="flex items-center justify-between" key={item.feature}>
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-gold-accent" strokeWidth={2.5} />
                <span className="text-gray-700 text-sm">{item.feature}</span>
              </div>
              {item.value > 0 && (
                <span className="text-gray-400 text-sm line-through">${item.value}</span>
              )}
            </div>
          ))}
        </div>

        {/* Total Value vs Price */}
        <div className="mt-4 border-gray-200 border-t pt-4">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-gray-600 text-sm">Total value:</span>
            <span className="text-gray-400 text-sm line-through">${plan.totalValue}/mo</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-semibold text-gray-900">Your price:</span>
            <span className="font-bold text-gold-accent text-lg">${plan.price}/mo</span>
          </div>
          <div className="mt-2 rounded-lg bg-green-50 px-3 py-2 text-center">
            <span className="font-semibold text-green-700 text-sm">
              You save ${savings}/mo ({savingsPercent}% off)
            </span>
          </div>
        </div>
      </div>

      {/* Risk Reversal / Guarantee */}
      <div className="mb-6 flex w-full max-w-md items-center justify-center gap-2">
        <Shield className="h-4 w-4 text-[#5BA4D4]" />
        <p className="text-gray-500 text-sm">Cancel anytime. No long-term contracts.</p>
      </div>

      <Button
        className="mb-4 w-full max-w-md bg-gold-accent font-semibold text-base text-gray-900 transition-all hover:bg-[#b08f54] focus:ring-2 focus:ring-gold-accent focus:ring-offset-2 focus:ring-offset-white sm:text-lg"
        onClick={handleContinue}
        size="lg"
      >
        Get Started Now
      </Button>

      <button
        className="font-medium text-gold-accent text-sm transition-opacity hover:opacity-80 focus:outline-hidden focus:ring-2 focus:ring-gold-accent focus:ring-offset-2 focus:ring-offset-white"
        onClick={handleChangePlan}
        type="button"
      >
        Compare other plans
      </button>

      {/* Anchor - Traditional cost comparison */}
      <div className="mt-6 w-full max-w-md rounded-lg bg-gray-50 p-4">
        <div className="flex items-start gap-3">
          <Zap className="mt-0.5 h-5 w-5 text-gold-accent" />
          <div>
            <p className="text-gray-700 text-sm">
              <strong>Traditional rendering:</strong> $200-500 per image, 2-5 day wait
            </p>
            <p className="mt-1 text-gray-700 text-sm">
              <strong>With Aideco:</strong> ~$0.07 per render, 60 seconds
            </p>
            <p className="mt-2 text-gray-500 text-xs">
              One project usually pays for a full year of Aideco.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
